/**
 * Module context factory.
 *
 * Creates the context injected into all Nexus modules, providing
 * database access, utilities, services registry, and entity factories.
 *
 * Structure:
 * - ctx.core    - Logger, errors, crypto, socket, events, middleware
 * - ctx.db      - Knex connection, adapters, migration helpers
 * - ctx.runtime - Entity service/controller/router factories
 * - ctx.config  - Environment variables and resolved NexusConfig
 * - ctx.engine  - Module/plugin introspection
 * - ctx.services - Inter-module service registry
 * - ctx.adapters - External data source adapters
 */

import { ForbiddenError as CASLForbiddenError, subject } from '@casl/ability'

// SDK types
import type {
  ModuleContext,
  CoreContext,
  DbContext,
  RuntimeContext,
  ConfigContext,
  EngineContext,
  ServicesContext,
  AdaptersContext,
  ModuleMiddlewares,
  DatabaseAdapter,
  SchemaAdapter,
  EntityDefinition
} from '@gzl10/nexus-sdk'
import { DEFAULT_TENANT_ID, DEFAULT_LOCALES } from '@gzl10/nexus-sdk'
import type { LocaleConfig } from '@gzl10/nexus-sdk'

// Namespace imports
import * as core from '../../core/index.js'
import * as db from '@kernel/db/index.js'
import { getMemoryKnex, destroyMemoryKnex } from '@kernel/db/memory-knex.js'
import * as engineNs from './index.js'

// HTTP layer — Hono-based entity factory + middleware
import { createEntityRuntime } from '../../http/entity-factory.js'
import { createRateLimit as createHonoRateLimit } from '../../http/middleware/rate-limit.middleware.js'
import { requestTimeout as honoRequestTimeout } from '../../http/middleware/timeout.middleware.js'
import { validateBody } from '../../http/middleware/validate.middleware.js'
import { OpenAPIHono } from '@hono/zod-openapi'
import type { MiddlewareHandler } from 'hono'
import type { NexusEnv } from '../../http/types.js'
import type { ZodSchema } from 'zod'

// Config
import { getConfig, env } from '@kernel/config/env.js'

// Plugin ops
import * as pluginOps from '../../core/plugin-ops.js'
import { discoverPlugins } from '@kernel/config/load-config.js'

// Events API
import { createEventsApi } from './events-api.js'

// Capabilities
import { CapabilitiesRegistry } from './capabilities-registry.js'

// Seed
import { runModuleSeed } from '@kernel/db/seed-runner.js'

// Cache
import { CacheManager } from '@kernel/cache/cache-manager.js'
import { Redis } from 'ioredis'

// Types
import type { ModuleServices } from './types.js'

// Platform locales (set via setLocales before createModuleContext)
let platformLocales: LocaleConfig[] = DEFAULT_LOCALES

/** Set platform locales before creating context. Called by start(). */
export function setLocales(locales: LocaleConfig[]): void {
  platformLocales = locales
}

// Singleton CacheManager (shared across all contexts, one per server lifecycle)
let sharedCacheManager: CacheManager | null = null

function getSharedCacheManager(): CacheManager {
  if (!sharedCacheManager) {
    const redisUrl = env.REDIS_URL
    let redisClient: Redis | undefined
    let adapter: 'redis' | 'memory' = 'memory'

    if (redisUrl) {
      try {
        redisClient = new Redis(redisUrl, {
          lazyConnect: false,
          maxRetriesPerRequest: 3,
          retryStrategy: (times: number) => {
            if (times > 3) return null
            return Math.min(times * 200, 2000)
          }
        })

        redisClient.on('error', (err) => {
          core.logger.warn({ err: err.message }, 'Redis connection error')
        })
        redisClient.on('reconnecting', () => {
          core.logger.info('Redis reconnecting...')
        })

        adapter = 'redis'
        core.logger.info('CacheManager: using Redis adapter')
      } catch (err) {
        core.logger.warn(
          { err: (err as Error).message },
          'CacheManager: Redis unavailable, falling back to memory adapter'
        )
        redisClient = undefined
      }
    }

    sharedCacheManager = new CacheManager(
      core.nexusEvents as unknown as import('eventemitter2').EventEmitter2,
      core.logger.child({ service: 'cache' }),
      {
        adapter,
        redisClient,
        redisPrefix: env.REDIS_PREFIX
      }
    )
    sharedCacheManager.startPruneScheduler()
  }
  return sharedCacheManager
}

/**
 * Reset CacheManager (for tests and server restart).
 */
export async function resetCacheManager(): Promise<void> {
  await sharedCacheManager?.destroy()
  sharedCacheManager = null
}

// Singleton temp adapter (shared across all contexts so seeds persist into routing)
let sharedTempAdapter: DatabaseAdapter | null = null

function getSharedTempAdapter(): DatabaseAdapter {
  if (env.REDIS_URL) {
    if (!sharedTempAdapter) {
      sharedTempAdapter = db.createRedisAdapter({
        url: env.REDIS_URL,
        prefix: env.REDIS_PREFIX
      })
    }
    return sharedTempAdapter
  }
  if (!sharedTempAdapter) {
    sharedTempAdapter = db.createInMemoryAdapter()
  }
  return sharedTempAdapter!
}

/**
 * Reset shared adapters (for tests and server restart).
 */
export async function resetSharedAdapters(): Promise<void> {
  sharedTempAdapter = null
  await resetCacheManager()
  await destroyMemoryKnex()
}

/**
 * Creates the context injected into modules.
 *
 * @example
 * ```typescript
 * const myModule: ModuleManifest = {
 *   name: 'my-module',
 *   init: (ctx) => {
 *     ctx.core.logger.info('Module initialized')
 *     ctx.services.register('myService', createMyService(ctx))
 *   },
 *   migrate: async (ctx) => {
 *     const { knex } = ctx.db
 *     if (!await knex.schema.hasTable('my_table')) {
 *       await knex.schema.createTable('my_table', (t) => {
 *         t.uuid('id').primary()
 *         ctx.db.addTimestamps(t, knex)
 *       })
 *     }
 *   }
 * }
 * ```
 */
export function createModuleContext(): ModuleContext {
  // Internal registries
  const servicesRegistry = {} as ModuleServices
  const adaptersRegistry: Record<string, { data: DatabaseAdapter; schema?: SchemaAdapter }> = {}

  // Database setup
  const knex = db.getDb()
  const defaultAdapter = db.createKnexAdapter(knex)
  const defaultSchemaAdapter = db.createKnexSchemaAdapter(knex)

  // Register 'temp' adapter for TempEntityDefinition
  // Uses Redis if REDIS_URL is defined, otherwise falls back to InMemory
  // Shared singleton so data persists across context instances (seed → routing)
  adaptersRegistry['temp'] = { data: getSharedTempAdapter() }
  core.logger.trace(env.REDIS_URL ? 'Temp adapter: Redis (shared)' : 'Temp adapter: InMemory (shared)')

  // Note: 'memory' adapter is now resolved via ctx.db.getKnex('memory') (SQLite :memory:)

  // Middleware factories (Hono-native; see src/http/middleware/).
  // `validate` here accepts a ValidateSchemas shape ({body?, query?, params?})
  // but only the body branch is wired — modules that need query/param validation
  // should import validateQuery/validateParam directly from http/middleware.
  const validateShim: ModuleMiddlewares['validate'] = (schemas) => {
    if (schemas.body) return validateBody(schemas.body as unknown as ZodSchema)
    // No-op passthrough when only query/params are declared here.
    const noop: MiddlewareHandler<NexusEnv> = async (_c, next) => { await next() }
    return noop
  }
  const middleware: ModuleMiddlewares = {
    validate: validateShim,
    rateLimit: createHonoRateLimit,
    requestTimeout: honoRequestTimeout
  }

  // ==========================================================================
  // Core namespace
  // ==========================================================================

  // Tenant-scoped logger: all downstream logs include tenantId
  const tenantLogger = core.logger.child({ tenantId: DEFAULT_TENANT_ID })

  const coreContext: CoreContext = {
    logger: tenantLogger,

    errors: {
      AppError: core.AppError,
      NotFoundError: core.NotFoundError,
      UnauthorizedError: core.UnauthorizedError,
      ForbiddenError: core.ForbiddenError,
      ConflictError: core.ConflictError,
      ValidationError: core.ValidationError,
      codes: core.ErrorCodes
    },

    crypto: {
      hashPassword: core.hashPassword,
      verifyPassword: core.verifyPassword,
      DUMMY_HASH: core.DUMMY_HASH,
      encrypt: core.encrypt,
      decrypt: core.decrypt
    },

    cache: getSharedCacheManager().scoped('app'),

    socket: {
      getIO: core.getIO,
      isInitialized: core.isSocketIOInitialized,
      isUserConnected: core.isUserConnected,
      getUserSocketCount: core.getUserSocketCount,
      getConnectedUsers: core.getConnectedUsers,
      joinUserToRoom: core.joinUserToRoom,
      removeUserFromRoom: core.removeUserFromRoom,
      joinSocketToRoom: core.joinSocketToRoom,
      removeSocketFromRoom: core.removeSocketFromRoom,
      getRoomMembers: core.getRoomMembers,
      getRoomSize: core.getRoomSize,
      getUserRooms: core.getUserRooms,
      roomExists: core.roomExists,
      emitToRoom: core.emitToRoom,
      emitToUser: core.emitToUser,
      emitToRole: core.emitToRole,
      emitToAll: core.emitToAll,
      emitToAuthenticated: core.emitToAuthenticated,
      broadcastToRoom: core.broadcastToRoom,
      onReady: core.onSocketReady
    },

    abilities: {
      defineAbilityFor: core.defineAbilityFor as CoreContext['abilities']['defineAbilityFor'],
      packRules: core.packRules as CoreContext['abilities']['packRules'],
      subject,
      ForbiddenError: CASLForbiddenError
    },

    events: core.nexusEvents as unknown as CoreContext['events'],

    hub: {
      registerRule: core.eventBridge.registerRule.bind(core.eventBridge),
      removeRule: core.eventBridge.removeRule.bind(core.eventBridge)
    },

    middleware,

    createRouter: () => new OpenAPIHono<NexusEnv>() as never,

    generateId: core.generateId,
    generateIdByType: core.generateIdByType,
    getLibPath: core.getLibPath,
    getProjectPath: core.getProjectPath,
    safeJsonParse: <T>(jsonString: string, fallback: T, context?: Record<string, unknown>): T =>
      core.safeJsonParse(core.logger, jsonString, fallback, context),

    plugins: {
      getState: pluginOps.getPluginState,
      getAllStates: pluginOps.getAllPluginStates,
      install: pluginOps.installPlugin,
      uninstall: pluginOps.uninstallPlugin,
      enable: pluginOps.enablePlugin,
      disable: pluginOps.disablePlugin,
      normalizeName: pluginOps.normalizePluginName,
      shortName: pluginOps.shortPluginName,
      discover: () => discoverPlugins(core.getProjectPath()),
    }
  }

  // ==========================================================================
  // Database namespace
  // ==========================================================================

  // Knex connections for adapters (memory = SQLite :memory:)
  const knexConnections: Record<string, import('knex').Knex> = {
    memory: getMemoryKnex()
  }

  const dbContext: DbContext = {
    knex,
    t: (name: string) => name,
    adapter: defaultAdapter,
    schema: defaultSchemaAdapter,

    // Migration helpers
    addTimestamps: db.addTimestamps,
    addAuditFieldsIfMissing: db.addAuditFieldsIfMissing,
    addSoftDeleteFieldIfMissing: db.addSoftDeleteFieldIfMissing,
    addConfigDefaultField: db.addConfigDefaultField,
    addColumnIfMissing: db.addColumnIfMissing,

    // Timestamp helpers
    nowTimestamp: db.nowTimestamp,
    formatTimestamp: db.formatTimestamp,

    // Query helpers
    applySearchFilter: db.applySearchFilter,
    getPagination: db.getPagination,
    buildPaginatedResult: db.buildPaginatedResult,

    // Adapter-aware Knex resolver
    getKnex(adapter?: string) {
      if (!adapter) return knex
      const conn = knexConnections[adapter]
      if (!conn) {
        throw new Error(`Knex connection for adapter "${adapter}" not found. Available: ${Object.keys(knexConnections).join(', ')}`)
      }
      return conn
    },

    // Placeholder — bound after ctx construction (needs full ModuleContext)
    seedModule: null as unknown as DbContext['seedModule']
  }

  // ==========================================================================
  // Runtime namespace
  // ==========================================================================
  // Factory functions use `this: ModuleContext` so they are bound after context creation

  // ==========================================================================
  // Config namespace
  // ==========================================================================
  const configContext: ConfigContext = {
    env,
    resolved: getConfig() as unknown as Record<string, unknown>
  }

  // ==========================================================================
  // Engine namespace
  // ==========================================================================
  const engineContext: EngineContext = {
    getModules: engineNs.getModules,
    getPlugins: engineNs.getPlugins,
    getModuleSubjects: engineNs.getModuleSubjects,
    getRegisteredSubjects: engineNs.getRegisteredSubjects,
    getCoreModules: engineNs.getCoreModules,
    getUserModules: engineNs.getUserModules,
    getCoreManifest: engineNs.getCoreManifest,
    getUserManifest: engineNs.getUserManifest,
    hasUserApp: engineNs.hasUserApp,
    getSubjectForTable: engineNs.getSubjectForTable,
    hasModule: engineNs.hasModule,
    hasPlugin: engineNs.hasPlugin,
    getPluginByCode: engineNs.getPluginByCode,
    hasPluginByCode: engineNs.hasPluginByCode,
    getEntityDefinition: engineNs.getEntityDefinition
  }

  // ==========================================================================
  // Services namespace (with Proxy for direct access)
  // ==========================================================================
  const servicesMethods = {
    register(name: string, service: unknown): void {
      servicesRegistry[name] = service
    },

    get<T>(name: string): T {
      const service = servicesRegistry[name]
      if (!service) {
        throw new Error(`Service "${name}" not initialized. Ensure the module loads before accessing it.`)
      }
      return service as T
    },

    getOptional<T>(name: string): T | undefined {
      return servicesRegistry[name] as T | undefined
    },

    has(name: string): boolean {
      return !!servicesRegistry[name]
    },

    getBySuffix<T>(suffix: string): Array<{ name: string; service: T }> {
      return Object.entries(servicesRegistry)
        .filter(([name]) => name.endsWith(suffix))
        .map(([name, service]) => ({ name, service: service as T }))
    }
  }

  // Proxy for direct service access: ctx.services.users, ctx.services['mail.provider']
  const servicesContext = new Proxy(servicesMethods, {
    get(target, prop: string) {
      // First check if it's a method on the base object
      if (prop in target) {
        return target[prop as keyof typeof target]
      }
      // Otherwise, direct service access
      return servicesRegistry[prop]
    },
    set(_, prop: string, value) {
      servicesRegistry[prop] = value
      return true
    }
  }) as ServicesContext

  // ==========================================================================
  // Adapters namespace
  // ==========================================================================
  const adaptersContext: AdaptersContext = {
    register(name: string, dataAdapter: DatabaseAdapter, schemaAdapter?: SchemaAdapter): void {
      if (adaptersRegistry[name]) {
        core.logger.warn({ adapter: name }, 'Adapter already registered, overwriting')
      }
      adaptersRegistry[name] = { data: dataAdapter, schema: schemaAdapter }
      core.logger.debug({ adapter: name, hasSchema: !!schemaAdapter }, 'Adapter registered')
    },

    get<T extends DatabaseAdapter = DatabaseAdapter>(name?: string): T {
      if (!name) return defaultAdapter as T
      const pair = adaptersRegistry[name]
      if (!pair) {
        throw new Error(`Adapter "${name}" not found. Available: ${Object.keys(adaptersRegistry).join(', ') || 'none'}`)
      }
      return pair.data as T
    },

    getSchema<T extends SchemaAdapter = SchemaAdapter>(name?: string): T | undefined {
      if (!name) return defaultSchemaAdapter as T
      const pair = adaptersRegistry[name]
      if (!pair) return undefined
      return pair.schema as T | undefined
    },

    has(name: string): boolean {
      return name in adaptersRegistry
    }
  }

  // ==========================================================================
  // Capabilities registry
  // ==========================================================================
  const capabilitiesRegistry = new CapabilitiesRegistry(
    core.logger.child({ service: 'capabilities' })
  )

  // ==========================================================================
  // Compose final context
  // ==========================================================================
  const ctx: ModuleContext = {
    tenantId: DEFAULT_TENANT_ID,

    core: coreContext,
    db: dbContext,
    runtime: null as unknown as RuntimeContext, // Placeholder, set below
    config: configContext,
    engine: engineContext,
    services: servicesContext,
    adapters: adaptersContext,
    capabilities: capabilitiesRegistry,

    // Root-level shortcuts for frequently used utilities
    events: createEventsApi(core.nexusEvents, core.logger),
    createRouter: () => new OpenAPIHono<NexusEnv>() as never,
    locales: platformLocales
  }

  // Pre-register shared CacheManager so modules can access it via ctx.services
  servicesRegistry['cacheManager'] = getSharedCacheManager()

  // Pre-register migration status function so observability module can use it without importing db/
  servicesRegistry['showMigrationStatus'] = db.showMigrationStatus

  // Bind runtime factory functions to the context.
  // Wired to the Hono-based http/entity-factory (F3). createEntityRuntime
  // returns {service, controller, router} — we expose them as separate factory
  // methods to match the SDK RuntimeContext shape.
  ctx.runtime = {
    // Sync signature kept for SDK compat; internally delegates to async factory.
    // For entity types that require async init (tree/dag) use createEntityServiceAsync.
    createEntityService: ((_def: EntityDefinition, _opts?: unknown) => {
      throw new Error('ctx.runtime.createEntityService is sync-only — use createEntityServiceAsync for Hono-era factories')
    }) as RuntimeContext['createEntityService'],
    createEntityServiceAsync: (async (def: EntityDefinition, opts?: unknown) => {
      const rt = await createEntityRuntime(ctx, def, opts as never)
      return rt.service
    }) as RuntimeContext['createEntityServiceAsync'],
    createEntityController: ((_service, _def) => {
      throw new Error('ctx.runtime.createEntityController cannot be called standalone in Hono era — use createEntityServiceAsync + http/entity-factory')
    }) as RuntimeContext['createEntityController'],
    createEntityRouter: ((_controller, _def) => {
      throw new Error('ctx.runtime.createEntityRouter cannot be called standalone in Hono era — use createEntityServiceAsync + http/entity-factory')
    }) as RuntimeContext['createEntityRouter']
  }

  // Bind seed function (needs full ctx for entity auto-seed)
  ctx.db.seedModule = (mod) => runModuleSeed(mod, ctx)

  // Register core capabilities (version, plugins, locales)
  capabilitiesRegistry.register('version', () => engineNs.getCoreManifest().version)
  capabilitiesRegistry.register('plugins', () => engineNs.getPlugins().map(p => p.code))
  capabilitiesRegistry.register('locales', () => platformLocales)

  return ctx
}
